import { useEffect, useState } from "react";
import type { Order, OrderStatus } from "@/mocks/branch/orders";
import { useBranch } from "@/context/BranchContext";
import { roleMeta } from "@/config/roles";
import {
  ORDER_STATUS,
  PICKUP_METHODS,
  PAYMENT_METHODS,
  SOURCE_LABELS,
  vatBreakdown,
  buildTimeline,
  isInvoiceAvailable,
  nextStatusOf,
  hasAppFee,
  resolveAction,
  statusLabelOf,
  isDeliveryOrder,
  formatCountdown,
  CUSTOMER_DEBT_AMOUNT,
} from "../constants";
import InvoiceCard from "./InvoiceCard";
import ConfirmDialog from "./ConfirmDialog";

interface OrderDetailPanelProps {
  order: Order;
  onClose: () => void;
  onAction: (id: string, status: OrderStatus) => void;
  onRate: (id: string, rating: number) => void;
  onConfirmNotPicked: (id: string) => void;
}

export default function OrderDetailPanel({ order, onClose, onAction, onRate, onConfirmNotPicked }: OrderDetailPanelProps) {
  const { employee, can } = useBranch();
  const canUpdateStatus = can("updateOrderStatus");
  const status = ORDER_STATUS[order.status];
  const statusLabel = statusLabelOf(order, order.status);
  const action = resolveAction(order);
  const pickup = PICKUP_METHODS[order.pickupMethod];
  const payment = PAYMENT_METHODS[order.paymentMethod];
  const source = SOURCE_LABELS[order.source];
  const [confirming, setConfirming] = useState(false);
  const [hoverStars, setHoverStars] = useState(0);

  const isDelivery = isDeliveryOrder(order);
  const actorName = employee?.name || "الموظف";
  const actorRole = employee ? roleMeta(employee.role).short : "موظف";
  const timeline = buildTimeline(order, actorName, actorRole);

  const invoiceReady = isInvoiceAvailable(order);
  const isCashApp = order.source === "app" && order.paymentMethod === "cash";
  const isCashReceive = order.status === "ready" && isCashApp && !isDelivery;
  const receiveLabel = isDelivery ? "تم التسليم" : "تم الاستلام";
  const { net, vat } = vatBreakdown(order.total);

  const canRate = can("customerRatings") && order.status === "picked_up";

  // عدّاد مهلة الاستلام داخل تفاصيل طلب الكاش الجاهز + إجراءات الموظف بعد انتهائها
  const [pickupRemaining, setPickupRemaining] = useState<number | null>(
    isCashReceive ? (order.pickupRemainingSeconds ?? 900) : null,
  );
  const [keptReady, setKeptReady] = useState(false);
  const [notPickedConfirming, setNotPickedConfirming] = useState(false);

  useEffect(() => {
    if (pickupRemaining === null || pickupRemaining <= 0) return;
    const t = window.setTimeout(() => setPickupRemaining((r) => (r === null ? null : r - 1)), 1000);
    return () => window.clearTimeout(t);
  }, [pickupRemaining]);

  const pickupExpired = isCashReceive && pickupRemaining !== null && pickupRemaining <= 0;

  const debtAmount = order.customerDebtAmount ?? CUSTOMER_DEBT_AMOUNT;
  const walletDeducted = order.walletDeducted ?? 0;
  const walletShortfall =
    order.walletShortfall ?? Math.max(0, debtAmount - walletDeducted);

  const handleConfirmAction = () => {
    if (action) onAction(order.id, nextStatusOf(order));
    setConfirming(false);
  };

  const invoiceHint = (() => {
    if (invoiceReady) return null;
    if (order.status === "not_picked") {
      return order.source === "pos"
        ? "طلب POS لم يُستلم — لا تُصدر الفاتورة النهائية لهذا الطلب."
        : "طلب كاش لم يُستلم — لا تُصدر فاتورة نهائية، وتم تسجيل مديونية عدم الاستلام على محفظة العميل وفق إعدادات القسم.";
    }
    return `طلب كاش عبر التطبيق — تُصدر الفاتورة فقط بعد الضغط على «${receiveLabel}» وتحصيل المبلغ النقدي.`;
  })();

  return (
    <div className="bg-background-50 rounded-card border border-background-200 overflow-hidden animate-fade-slide-up">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 md:gap-3 px-3 sm:px-5 min-h-14 md:h-16 py-2 border-b border-background-200 bg-background-100">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-1.5 h-10 pl-2 pr-3 rounded-btn text-sm font-semibold text-foreground-600 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line text-xl"></i>
            الطلبات
          </button>
          <span className="text-lg font-bold text-foreground-900 whitespace-nowrap">#{order.number}</span>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap"
            style={{ backgroundColor: status.bg, color: status.color }}
          >
            <i className={status.icon}></i>
            {statusLabel}
          </span>
          <span className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-bold whitespace-nowrap">
            <i className={source.icon}></i>
            {source.short}
          </span>
        </div>
        {invoiceReady ? (
          <span className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-btn text-xs font-semibold text-primary-700 bg-primary-50 border border-primary-200 whitespace-nowrap">
            <i className="ri-receipt-line text-lg"></i>
            الفاتورة صادرة
          </span>
        ) : null}
      </div>

      <div className="px-4 sm:px-5 py-5 grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          {/* meta */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <MetaBox label="تاريخ ووقت الطلب" value={order.createdAt} dir="ltr" icon="ri-time-line" />
            <MetaBox label="طريقة الاستلام" value={pickup.label} icon={pickup.icon} />
            <MetaBox label="وسيلة الدفع" value={payment.label} icon={payment.icon} />
            <MetaBox label="مصدر الطلب" value={source.short} icon={source.icon} />
            <MetaBox label="الفرع" value={employee?.branchName || "فرع العليا"} icon="ri-map-pin-line" />
            <MetaBox
              label="اسم المحل"
              value={employee?.storeName || "مطعم الذوق الرفيع"}
              icon="ri-store-2-line"
            />
          </div>

          {/* customer / phone */}
          {order.source === "app" && order.customerName ? (
            <div className="bg-background-100 rounded-card border border-background-200 p-3.5 space-y-1.5">
              <p className="text-sm text-foreground-700">
                <span className="text-foreground-500">العميل: </span>
                <span className="font-semibold">{order.customerName}</span>
              </p>
              {order.customerPhone ? (
                <p className="text-sm text-foreground-700">
                  <span className="text-foreground-500">رقم الجوال: </span>
                  <a href={`tel:${order.customerPhone}`} className="font-semibold text-primary-700 hover:underline" dir="ltr">
                    {order.customerPhone}
                  </a>
                </p>
              ) : null}
            </div>
          ) : order.customerPhone ? (
            <div className="bg-background-100 rounded-card border border-background-200 p-3.5">
              <p className="text-sm text-foreground-700">
                <span className="text-foreground-500">رقم الجوال (طلب كاش): </span>
                <a href={`tel:${order.customerPhone}`} className="font-semibold text-primary-700 hover:underline" dir="ltr">
                  {order.customerPhone}
                </a>
              </p>
            </div>
          ) : null}

          {/* delivery address (display only — no couriers or tracking) */}
          {order.pickupMethod === "delivery" && order.deliveryAddress ? (
            <div className="bg-background-100 rounded-card border border-background-200 p-3.5">
              <p className="text-xs font-bold text-foreground-800 mb-2 flex items-center gap-1.5">
                <i className="ri-map-pin-2-line text-base text-primary-600"></i>
                عنوان التوصيل
              </p>
              <p className="text-sm text-foreground-800 leading-relaxed">{order.deliveryAddress}</p>
              <p className="text-[11px] text-foreground-500 mt-2">التوصيل بواسطة المحل — للعرض فقط</p>
            </div>
          ) : null}

          {/* car info */}
          {order.carInfo ? (
            <div className="bg-accent-50 rounded-card border border-accent-200 p-3.5">
              <p className="text-xs font-bold text-accent-800 mb-2 flex items-center gap-1.5">
                <i className="ri-car-line"></i>
                بيانات السيارة
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div>
                  <p className="text-xs text-foreground-500">نوع السيارة</p>
                  <p className="font-semibold text-foreground-900">{order.carInfo.type}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">اللون</p>
                  <p className="font-semibold text-foreground-900">{order.carInfo.color}</p>
                </div>
                <div>
                  <p className="text-xs text-foreground-500">رقم اللوحة</p>
                  <p className="font-semibold text-foreground-900">{order.carInfo.plate}</p>
                </div>
              </div>
            </div>
          ) : null}

          {/* notes */}
          {order.notes ? (
            <div className="bg-accent-50 rounded-card border border-accent-200 p-3.5">
              <p className="text-xs font-bold text-accent-800 mb-1.5 flex items-center gap-1.5">
                <i className="ri-sticky-note-line"></i>
                ملاحظات العميل
              </p>
              <p className="text-sm text-foreground-800 leading-relaxed">{order.notes}</p>
            </div>
          ) : null}

          {/* items + totals */}
          <div className="bg-background-100 rounded-card border border-background-200 p-4">
            <h4 className="text-sm font-bold text-foreground-900 mb-3">المنتجات والكميات</h4>
            <div className="space-y-2.5">
              {order.items.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm gap-3">
                  <span className="text-foreground-700 min-w-0 truncate">
                    <span className="font-bold text-primary-700 ml-1">{item.quantity}×</span>
                    {item.name}
                  </span>
                  <span className="text-foreground-400 text-xs whitespace-nowrap" dir="ltr">
                    {item.unitPrice.toFixed(2)} × {item.quantity}
                  </span>
                  <span className="text-foreground-700 font-semibold whitespace-nowrap w-20 text-left">
                    {(item.quantity * item.unitPrice).toFixed(2)} ر.س
                  </span>
                </div>
              ))}
            </div>
            <div className="border-t border-dashed border-background-300 mt-3 pt-3 space-y-1.5 text-sm">
              <TotalRow label="المجموع الفرعي" value={order.subtotal} />
              {order.discount > 0 ? <TotalRow label="الخصم" value={-order.discount} danger /> : null}
              {hasAppFee(order) ? <TotalRow label="رسوم التطبيق" value={order.appFee} /> : null}
              {order.deliveryFee && order.deliveryFee > 0 ? (
                <TotalRow label="رسوم التوصيل" value={order.deliveryFee} />
              ) : null}
              <div className="flex justify-between text-foreground-600">
                <span>صافي قبل الضريبة</span>
                <span>{net.toFixed(2)} ر.س</span>
              </div>
              <div className="flex justify-between text-foreground-600">
                <span>الضريبة المتضمنة (15%)</span>
                <span>{vat.toFixed(2)} ر.س</span>
              </div>
              <div className="flex justify-between font-bold text-foreground-900 text-base pt-1.5 border-t border-background-200">
                <span>الإجمالي شامل الضريبة</span>
                <span>{order.total.toFixed(2)} ر.س</span>
              </div>
            </div>

            {isCashApp && order.debtRecorded && order.status !== "not_picked" ? (
              <div className="mt-3 flex items-center justify-between gap-2 px-3.5 py-3 rounded-btn bg-accent-50 border border-accent-200">
                <span className="flex items-center gap-2 text-xs font-bold text-accent-800">
                  <i className="ri-alert-line text-base"></i>
                  مديونية رسوم التطبيق
                </span>
                <span className="text-sm font-bold text-accent-800 whitespace-nowrap">0.80 ر.س</span>
              </div>
            ) : null}

            {order.status === "not_picked" && isCashApp ? (
              <div className="mt-3 px-3.5 py-3 rounded-btn bg-accent-100 border border-accent-300 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-2 text-xs font-bold text-accent-900">
                    <i className="ri-user-unfollow-line text-base"></i>
                    مديونية العميل
                  </span>
                  <span className="text-base font-bold text-accent-900 whitespace-nowrap">
                    {debtAmount.toFixed(2)} ر.س
                  </span>
                </div>
                <div className="pt-2 border-t border-accent-300 space-y-1 text-[11px] text-accent-800">
                  <div className="flex items-center justify-between">
                    <span>المخصوم من محفظة العميل</span>
                    <span className="font-bold" dir="ltr">
                      {walletDeducted.toFixed(2)} ر.س
                    </span>
                  </div>
                  {walletShortfall > 0 ? (
                    <div className="flex items-center justify-between">
                      <span>المتبقي كرصيد سالب على المحفظة</span>
                      <span className="font-bold" dir="ltr">
                        {walletShortfall.toFixed(2)} ر.س
                      </span>
                    </div>
                  ) : null}
                  <p className="flex items-start gap-1.5 leading-relaxed pt-0.5">
                    <i className="ri-lock-line mt-0.5 shrink-0"></i>
                    تم إيقاف العميل عن إنشاء طلبات جديدة حتى سداد المديونية.
                  </p>
                  <p className="leading-relaxed">
                    مسجّل وفق القيمة المحددة من إدارة التطبيق لقسم المتجر — غير قابل للتعديل من الموظف.
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          {/* invoice */}
          {invoiceReady ? (
            <InvoiceCard order={order} />
          ) : (
            <div className="rounded-card border border-background-200 bg-background-100 p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-btn bg-background-200 text-foreground-500 flex items-center justify-center shrink-0">
                <i className="ri-lock-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-bold text-foreground-800">الفاتورة غير متاحة بعد</p>
                <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{invoiceHint}</p>
              </div>
            </div>
          )}
        </div>

        {/* Side column */}
        <div className="space-y-5">
          {/* timeline */}
          <div className="bg-background-100 rounded-card border border-background-200 p-4">
            <h4 className="text-sm font-bold text-foreground-900 mb-3">مسار حالة الطلب</h4>
            <div className="space-y-0">
              {timeline.map((step, idx) => {
                const isLast = idx === timeline.length - 1;
                const stepStatus = ORDER_STATUS[step.status];
                const done = step.isCompleted;
                return (
                  <div key={`${step.status}-${idx}`} className="relative flex gap-3 pb-4 last:pb-0">
                    {!isLast ? (
                      <span
                        className={`absolute top-5 right-[9px] bottom-0 w-0.5 ${
                          done ? "bg-background-300" : "bg-background-200"
                        }`}
                      ></span>
                    ) : null}
                    <div
                      className={`relative z-10 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        done ? "" : "bg-background-200 text-foreground-400"
                      }`}
                      style={done ? { backgroundColor: stepStatus.bg, color: stepStatus.color } : undefined}
                    >
                      <i className={`${done ? stepStatus.icon : "ri-time-line"} text-xs`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold ${done ? "text-foreground-900" : "text-foreground-500"}`}>
                        {step.label}
                      </p>
                      {done ? (
                        <p className="text-xs text-foreground-500 mt-0.5">
                          {step.actorName} · {step.actorRole} · {step.time}
                        </p>
                      ) : (
                        <p className="text-xs text-foreground-400 mt-0.5">بانتظار التنفيذ</p>
                      )}
                      {done && step.note ? (
                        <p className="mt-1.5 flex items-start gap-1.5 rounded-btn bg-background-200/60 px-2.5 py-1.5 text-[11px] leading-relaxed text-foreground-600">
                          <i className="ri-information-line mt-0.5 shrink-0 text-foreground-500"></i>
                          <span>{step.note}</span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* next action */}
          <div className="bg-background-100 rounded-card border border-background-200 p-4">
            {action && canUpdateStatus ? (
              <>
                {isCashReceive && pickupRemaining !== null ? (
                  <div
                    className={`flex items-center justify-between px-3.5 h-10 rounded-btn mb-3 ${
                      pickupRemaining <= 0
                        ? "bg-accent-100 text-accent-900"
                        : "bg-secondary-100 text-secondary-900"
                    }`}
                  >
                    <span className="text-xs font-semibold">وقت الاستلام المتبقي</span>
                    <span className="text-sm font-bold" dir="ltr">
                      {pickupRemaining <= 0 ? "0:00" : formatCountdown(pickupRemaining)}
                    </span>
                  </div>
                ) : null}

                <p className="text-xs text-foreground-500 mb-2.5 text-center leading-relaxed">
                  المرحلة الحالية: <strong className="text-foreground-800">{statusLabel}</strong>
                </p>

                {isCashReceive && pickupExpired && !keptReady ? (
                  <div className="space-y-2.5">
                    <button
                      type="button"
                      onClick={() => setNotPickedConfirming(true)}
                      className="w-full h-[52px] rounded-btn text-base font-bold bg-red-500 text-white hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      تأكيد عدم الاستلام
                    </button>
                    <button
                      type="button"
                      onClick={() => setKeptReady(true)}
                      className="w-full h-[52px] rounded-btn text-base font-bold border border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      إبقاء الطلب جاهزًا للاستلام
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirming(true)}
                    className="w-full h-[52px] rounded-btn text-base font-bold transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
                    style={{ backgroundColor: action.color, color: action.textColor }}
                  >
                    {action.label}
                  </button>
                )}

                {isCashReceive ? (
                  <p className="mt-2.5 text-[11px] text-foreground-500 text-center leading-relaxed">
                    بعد انتهاء مهلة الاستلام والمهلة الإضافية، يجب على موظف الفرع تأكيد عدم استلام
                    العميل للطلب.
                  </p>
                ) : order.status === "ready" && !isDelivery ? (
                  <p className="mt-2.5 text-[11px] text-foreground-500 text-center leading-relaxed">
                    إن انتهت مهلة الاستلام والمهلة الإضافية ينفّذ النظام حالة «لم يُستلم» تلقائياً.
                  </p>
                ) : null}
              </>
            ) : !canUpdateStatus ? (
              <p className="min-h-[52px] flex items-center justify-center text-sm font-semibold text-foreground-500 text-center leading-relaxed px-2">
                يمكنك مشاهدة الطلب فقط — لا تملك صلاحية تحديث حالات الطلبات.
              </p>
            ) : (
              <p className="min-h-[52px] flex items-center justify-center text-sm font-semibold text-foreground-500 text-center leading-relaxed px-2">
                {order.status === "picked_up"
                  ? isDelivery
                    ? "تم تسليم الطلب بنجاح — لا يوجد إجراء إضافي"
                    : "تم استلام الطلب بنجاح — لا يوجد إجراء إضافي"
                  : "حالة مغلقة «لم يُستلم» — لا يوجد إجراء تحديث"}
              </p>
            )}
          </div>

          {/* rating */}
          {canRate ? (
            <div className="bg-background-100 rounded-card border border-background-200 p-4">
              <h4 className="text-sm font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-user-star-line text-accent-600"></i>
                تقييم العميل
              </h4>
              {order.staffRating ? (
                <>
                  <div className="flex items-center gap-1.5 mt-3" dir="ltr">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i
                        key={s}
                        className={`text-xl ${s <= order.staffRating! ? "ri-star-fill text-accent-500" : "ri-star-line text-background-400"}`}
                      ></i>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-foreground-500">
                    تم تقييم العميل — {order.staffRatedBy} · {order.staffRatedAt}
                  </p>
                  <p className="mt-1 text-[11px] text-foreground-400">
                    لا يمكن تقييم الطلب أكثر من مرة.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-xs text-foreground-500 mt-1.5 leading-relaxed">
                    قيّم تجربة العميل في هذا الطلب (من نجمة إلى خمس).
                  </p>
                  <div className="flex items-center gap-1 mt-3" dir="ltr">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        type="button"
                        aria-label={`${s} نجوم`}
                        onMouseEnter={() => setHoverStars(s)}
                        onMouseLeave={() => setHoverStars(0)}
                        onClick={() => onRate(order.id, s)}
                        className="w-10 h-10 flex items-center justify-center cursor-pointer"
                      >
                        <i
                          className={`text-2xl ${hoverStars >= s ? "ri-star-fill text-accent-500" : "ri-star-line text-background-400"}`}
                        ></i>
                      </button>
                    ))}
                  </div>
                </>
              )}
              {typeof order.rating === "number" ? (
                <p className="mt-3 pt-3 border-t border-background-200 text-xs text-foreground-500">
                  تقييم العميل للمحل: {order.rating}/5
                </p>
              ) : null}
            </div>
          ) : order.status === "picked_up" && typeof order.rating === "number" ? (
            <div className="bg-background-100 rounded-card border border-background-200 p-4">
              <h4 className="text-sm font-bold text-foreground-900">تقييم العميل للمحل</h4>
              <div className="flex items-center gap-1 mt-2" dir="ltr">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i
                    key={s}
                    className={`text-xl ${s <= (order.rating ?? 0) ? "ri-star-fill text-accent-500" : "ri-star-line text-background-400"}`}
                  ></i>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {confirming && action ? (
        <ConfirmDialog
          title={
            isCashReceive
              ? "هل تم تسليم الطلب للعميل وتحصيل المبلغ النقدي؟"
              : action.confirmTitle
          }
          message={
            isCashReceive
              ? "بعد التأكيد تصبح حالة الطلب «مُستلَم» وتُصدر الفاتورة الضريبية المبسطة."
              : action.confirmMessage
          }
          confirmLabel={isCashReceive ? "تأكيد الاستلام والتحصيل" : action.label}
          cancelLabel="رجوع"
          tone={isCashReceive || order.status === "ready" ? "success" : "primary"}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirming(false)}
        />
      ) : null}

      {notPickedConfirming ? (
        <ConfirmDialog
          title="تأكيد عدم الاستلام"
          message="هل تؤكد أن العميل لم يستلم الطلب؟ سيؤدي التأكيد إلى تغيير حالة الطلب إلى لم يُستلم وتسجيل المديونية على محفظة العميل وفق القيمة المحددة لهذا القسم."
          confirmLabel="تأكيد عدم الاستلام"
          cancelLabel="تراجع"
          tone="danger"
          onConfirm={() => {
            onConfirmNotPicked(order.id);
            setNotPickedConfirming(false);
          }}
          onCancel={() => setNotPickedConfirming(false)}
        />
      ) : null}
    </div>
  );
}

function MetaBox({
  label,
  value,
  icon,
  dir,
}: {
  label: string;
  value: string;
  icon: string;
  dir?: "ltr";
}) {
  return (
    <div className="rounded-btn bg-background-100 border border-background-200 p-3">
      <p className="text-[11px] text-foreground-500 flex items-center gap-1.5">
        <i className={icon}></i>
        {label}
      </p>
      <p className="text-sm font-semibold text-foreground-900 mt-1 truncate" dir={dir}>
        {value}
      </p>
    </div>
  );
}

function TotalRow({ label, value, danger }: { label: string; value: number; danger?: boolean }) {
  return (
    <div className={`flex justify-between ${danger ? "text-red-600" : "text-foreground-600"}`}>
      <span>{label}</span>
      <span>
        {value < 0 ? "-" : ""}
        {Math.abs(value).toFixed(2)} ر.س
      </span>
    </div>
  );
}